/**
 * BrainGraph — Canvas-based neural network brain visualization.
 *
 * Renders a brain silhouette filled with a dense mesh of glowing nodes
 * and interconnecting lines, like a digital neural network map.
 * Data nodes from the cognitive profile are embedded as colored anchor nodes
 * within their anatomical brain zones.
 */
import React, {
  useEffect, useRef, useMemo, useState, useCallback,
} from 'react';

// ─── Types ───────────────────────────────────────────────────────────────────
interface BrainNode {
  id: string; type: string; value: string;
  confidence: number; source: string; hemisphere: string; region: string;
}
interface RegionStat { region: string; hemisphere: string; nodeCount: number; avgConfidence: number; }
interface Conflict {
  nodeA: { id: string; value: string; type: string; region: string };
  nodeB: { id: string; value: string; type: string; region: string };
  reason: string;
}
interface Props {
  nodes: BrainNode[]; regionStats: RegionStat[]; coverage: number;
  conflicts: Conflict[]; highImpact: BrainNode[];
  onNodeClick?: (node: BrainNode) => void;
  onAddNode?: (region: string) => void;
}

// ─── Region anchors (anatomical positions, canvas space 960×700) ─────────────
const CANVAS_W = 960, CANVAS_H = 700;
const CX = CANVAS_W / 2, CY = 330;

const REGIONS: Record<string, {cx:number; cy:number; label:string; color:string; rgb:[number,number,number]}> = {
  identity:      {cx:270, cy:170, label:'Identity',      color:'#67e8f9', rgb:[103,232,249]},
  skills:        {cx:165, cy:330, label:'Skills',         color:'#4ade80', rgb:[74,222,128]},
  governance:    {cx:240, cy:480, label:'Governance',     color:'#fcd34d', rgb:[252,211,77]},
  communication: {cx:690, cy:170, label:'Communication',  color:'#c084fc', rgb:[192,132,252]},
  emotion:       {cx:795, cy:330, label:'Emotion',        color:'#f472b6', rgb:[244,114,182]},
  memory:        {cx:720, cy:480, label:'Memory',         color:'#38bdf8', rgb:[56,189,248]},
  ethics:        {cx: CX, cy:540, label:'Ethics',         color:'#fb923c', rgb:[251,146,60]},
};

const TYPE_COLORS: Record<string,string> = {
  fact:'#67e8f9', preference:'#c084fc', behavior:'#4ade80',
  boundary:'#f87171', evidence:'#fcd34d',
};

// ─── Neural node (background mesh) ──────────────────────────────────────────
interface NeuralPt {
  x: number; y: number;
  vx: number; vy: number;   // slow drift velocity
  baseAlpha: number;
  phase: number;             // animation phase offset
  region: string | null;     // null = background mesh node
  dataNode?: BrainNode;      // if this is a data node
  r: number;                 // radius
  rgb: [number,number,number];
}

// ─── Brain silhouette paths (top-down anatomical brain with deep gyri) ───────
// Closely modeled on the holographic wireframe reference: rounder, very wrinkled
// outline, deep longitudinal fissure, pronounced gyri bumps on all sides.

function buildLeftHemisphere(ctx: CanvasRenderingContext2D | Path2D): void {
  const p = ctx instanceof Path2D ? ctx : null;
  const c = (method: string, ...args: number[]) => {
    if (p) (p as any)[method](...args);
    else (ctx as any)[method](...args);
  };

  // Start at top of fissure (left side of gap)
  c('moveTo', CX - 10, 72);

  // ── Frontal lobe (top → upper-left) — with 3 gyrus bumps ──
  c('bezierCurveTo', CX - 28, 58, CX - 55, 50, CX - 88, 48);
  // Gyrus F1 — subtle bump
  c('bezierCurveTo', CX - 105, 42, CX - 120, 38, CX - 140, 42);
  c('bezierCurveTo', CX - 158, 46, CX - 170, 50, CX - 188, 44);
  // Gyrus F2 — deeper bump  
  c('bezierCurveTo', CX - 210, 38, CX - 228, 42, CX - 248, 52);
  c('bezierCurveTo', CX - 265, 60, CX - 278, 56, CX - 295, 62);
  // Gyrus F3 — frontal-lateral bump
  c('bezierCurveTo', CX - 318, 72, CX - 335, 68, CX - 352, 80);

  // ── Pre-temporal (upper-left → side) — with 2 bumps ──
  c('bezierCurveTo', CX - 370, 94, CX - 382, 108, CX - 392, 128);
  // Gyrus T1
  c('bezierCurveTo', CX - 400, 146, CX - 406, 158, CX - 402, 175);
  c('bezierCurveTo', CX - 410, 192, CX - 418, 210, CX - 420, 230);
  // Gyrus T2 — widest temporal bulge
  c('bezierCurveTo', CX - 424, 248, CX - 426, 262, CX - 422, 282);
  c('bezierCurveTo', CX - 428, 300, CX - 430, 318, CX - 426, 340);

  // ── Parietal (widest lateral → lower-side) — with 3 bumps ──
  // Gyrus P1
  c('bezierCurveTo', CX - 428, 358, CX - 424, 372, CX - 418, 388);
  c('bezierCurveTo', CX - 412, 402, CX - 420, 412, CX - 414, 428);
  // Gyrus P2 — parietal bump
  c('bezierCurveTo', CX - 406, 442, CX - 394, 452, CX - 380, 462);
  c('bezierCurveTo', CX - 368, 470, CX - 358, 464, CX - 342, 474);
  // Gyrus P3 — occipital transition
  c('bezierCurveTo', CX - 322, 484, CX - 298, 492, CX - 270, 498);

  // ── Occipital lobe (bottom-side → center-bottom) — with 2 bumps ──
  c('bezierCurveTo', CX - 242, 504, CX - 218, 500, CX - 192, 506);
  // Gyrus O1
  c('bezierCurveTo', CX - 166, 512, CX - 144, 508, CX - 118, 514);
  // Gyrus O2
  c('bezierCurveTo', CX - 92, 518, CX - 68, 520, CX - 48, 518);
  c('bezierCurveTo', CX - 30, 516, CX - 18, 512, CX - 10, 504);

  // ── Brainstem nub ──
  c('bezierCurveTo', CX - 14, 520, CX - 16, 540, CX - 12, 558);
  c('bezierCurveTo', CX - 8, 570, CX - 4, 576, CX, 580);
  c('lineTo', CX, 580);

  // ── Return along inner fissure edge ──
  c('lineTo', CX - 10, 504);
  c('bezierCurveTo', CX - 12, 420, CX - 12, 300, CX - 11, 190);
  c('bezierCurveTo', CX - 11, 140, CX - 10, 100, CX - 10, 72);
  c('closePath');
}

function buildRightHemisphere(ctx: CanvasRenderingContext2D | Path2D): void {
  const p = ctx instanceof Path2D ? ctx : null;
  const c = (method: string, ...args: number[]) => {
    if (p) (p as any)[method](...args);
    else (ctx as any)[method](...args);
  };

  // Start at top of fissure (right side of gap)
  c('moveTo', CX + 10, 72);

  // ── Frontal lobe — mirrored bumps ──
  c('bezierCurveTo', CX + 28, 58, CX + 55, 50, CX + 88, 48);
  c('bezierCurveTo', CX + 105, 42, CX + 120, 38, CX + 140, 42);
  c('bezierCurveTo', CX + 158, 46, CX + 170, 50, CX + 188, 44);
  c('bezierCurveTo', CX + 210, 38, CX + 228, 42, CX + 248, 52);
  c('bezierCurveTo', CX + 265, 60, CX + 278, 56, CX + 295, 62);
  c('bezierCurveTo', CX + 318, 72, CX + 335, 68, CX + 352, 80);

  // ── Pre-temporal ──
  c('bezierCurveTo', CX + 370, 94, CX + 382, 108, CX + 392, 128);
  c('bezierCurveTo', CX + 400, 146, CX + 406, 158, CX + 402, 175);
  c('bezierCurveTo', CX + 410, 192, CX + 418, 210, CX + 420, 230);
  c('bezierCurveTo', CX + 424, 248, CX + 426, 262, CX + 422, 282);
  c('bezierCurveTo', CX + 428, 300, CX + 430, 318, CX + 426, 340);

  // ── Parietal ──
  c('bezierCurveTo', CX + 428, 358, CX + 424, 372, CX + 418, 388);
  c('bezierCurveTo', CX + 412, 402, CX + 420, 412, CX + 414, 428);
  c('bezierCurveTo', CX + 406, 442, CX + 394, 452, CX + 380, 462);
  c('bezierCurveTo', CX + 368, 470, CX + 358, 464, CX + 342, 474);
  c('bezierCurveTo', CX + 322, 484, CX + 298, 492, CX + 270, 498);

  // ── Occipital lobe ──
  c('bezierCurveTo', CX + 242, 504, CX + 218, 500, CX + 192, 506);
  c('bezierCurveTo', CX + 166, 512, CX + 144, 508, CX + 118, 514);
  c('bezierCurveTo', CX + 92, 518, CX + 68, 520, CX + 48, 518);
  c('bezierCurveTo', CX + 30, 516, CX + 18, 512, CX + 10, 504);

  // ── Brainstem nub ──
  c('bezierCurveTo', CX + 14, 520, CX + 16, 540, CX + 12, 558);
  c('bezierCurveTo', CX + 8, 570, CX + 4, 576, CX, 580);
  c('lineTo', CX, 580);

  // ── Return along inner fissure edge ──
  c('lineTo', CX + 10, 504);
  c('bezierCurveTo', CX + 12, 420, CX + 12, 300, CX + 11, 190);
  c('bezierCurveTo', CX + 11, 140, CX + 10, 100, CX + 10, 72);
  c('closePath');
}

// Combined path (for overall clipping/hit-tests)
function buildBrainPath(ctx: CanvasRenderingContext2D | Path2D): void {
  buildLeftHemisphere(ctx);
  buildRightHemisphere(ctx);
}

// ─── Sulci (cortical fold lines inside each hemisphere) ──────────────────────
// Dense fold pattern matching the holographic wireframe reference.
interface Sulcus {
  side: 'left' | 'right';
  points: [number, number, number, number, number, number][];
}

const SULCI: Sulcus[] = [
  // ═══ LEFT HEMISPHERE ═══
  // Central sulcus (Rolando) — the big diagonal
  { side: 'left', points: [
    [CX - 30, 120, CX - 80, 150, CX - 150, 185],
    [CX - 150, 185, CX - 220, 220, CX - 310, 270],
  ]},
  // Precentral sulcus
  { side: 'left', points: [
    [CX - 40, 100, CX - 65, 140, CX - 90, 200],
    [CX - 90, 200, CX - 110, 250, CX - 130, 310],
  ]},
  // Postcentral sulcus
  { side: 'left', points: [
    [CX - 120, 110, CX - 180, 160, CX - 230, 220],
    [CX - 230, 220, CX - 270, 270, CX - 300, 330],
  ]},
  // Lateral / Sylvian fissure
  { side: 'left', points: [
    [CX - 20, 320, CX - 120, 338, CX - 240, 330],
    [CX - 240, 330, CX - 330, 325, CX - 400, 340],
  ]},
  // Superior frontal sulcus
  { side: 'left', points: [
    [CX - 30, 80, CX - 100, 75, CX - 210, 65],
    [CX - 210, 65, CX - 280, 70, CX - 340, 82],
  ]},
  // Inferior frontal sulcus
  { side: 'left', points: [
    [CX - 50, 160, CX - 130, 148, CX - 240, 150],
    [CX - 240, 150, CX - 310, 155, CX - 370, 175],
  ]},
  // Superior temporal sulcus
  { side: 'left', points: [
    [CX - 35, 400, CX - 130, 415, CX - 250, 410],
    [CX - 250, 410, CX - 340, 400, CX - 400, 410],
  ]},
  // Inferior temporal sulcus
  { side: 'left', points: [
    [CX - 30, 450, CX - 110, 465, CX - 220, 460],
    [CX - 220, 460, CX - 300, 455, CX - 370, 465],
  ]},
  // Intraparietal sulcus
  { side: 'left', points: [
    [CX - 70, 260, CX - 150, 275, CX - 240, 270],
    [CX - 240, 270, CX - 320, 265, CX - 390, 285],
  ]},
  // Parieto-occipital sulcus
  { side: 'left', points: [
    [CX - 40, 470, CX - 90, 490, CX - 160, 500],
  ]},
  // Calcarine sulcus
  { side: 'left', points: [
    [CX - 15, 490, CX - 80, 500, CX - 170, 505],
    [CX - 170, 505, CX - 230, 500, CX - 280, 498],
  ]},

  // ═══ RIGHT HEMISPHERE (mirrored) ═══
  { side: 'right', points: [
    [CX + 30, 120, CX + 80, 150, CX + 150, 185],
    [CX + 150, 185, CX + 220, 220, CX + 310, 270],
  ]},
  { side: 'right', points: [
    [CX + 40, 100, CX + 65, 140, CX + 90, 200],
    [CX + 90, 200, CX + 110, 250, CX + 130, 310],
  ]},
  { side: 'right', points: [
    [CX + 120, 110, CX + 180, 160, CX + 230, 220],
    [CX + 230, 220, CX + 270, 270, CX + 300, 330],
  ]},
  { side: 'right', points: [
    [CX + 20, 320, CX + 120, 338, CX + 240, 330],
    [CX + 240, 330, CX + 330, 325, CX + 400, 340],
  ]},
  { side: 'right', points: [
    [CX + 30, 80, CX + 100, 75, CX + 210, 65],
    [CX + 210, 65, CX + 280, 70, CX + 340, 82],
  ]},
  { side: 'right', points: [
    [CX + 50, 160, CX + 130, 148, CX + 240, 150],
    [CX + 240, 150, CX + 310, 155, CX + 370, 175],
  ]},
  { side: 'right', points: [
    [CX + 35, 400, CX + 130, 415, CX + 250, 410],
    [CX + 250, 410, CX + 340, 400, CX + 400, 410],
  ]},
  { side: 'right', points: [
    [CX + 30, 450, CX + 110, 465, CX + 220, 460],
    [CX + 220, 460, CX + 300, 455, CX + 370, 465],
  ]},
  { side: 'right', points: [
    [CX + 70, 260, CX + 150, 275, CX + 240, 270],
    [CX + 240, 270, CX + 320, 265, CX + 390, 285],
  ]},
  { side: 'right', points: [
    [CX + 40, 470, CX + 90, 490, CX + 160, 500],
  ]},
  { side: 'right', points: [
    [CX + 15, 490, CX + 80, 500, CX + 170, 505],
    [CX + 170, 505, CX + 230, 500, CX + 280, 498],
  ]},
];

// ─── Point-in-brain test ─────────────────────────────────────────────────────
function insideBrain(x: number, y: number): boolean {
  const dx = x - CX, dy = y - CY;
  // Main brain oval (slightly taller now)
  const inMain = (dx * dx) / (425 * 425) + (dy * dy) / (265 * 265) <= 1;
  // Brainstem zone
  const inStem = Math.abs(dx) < 14 && y > CY + 180 && y < 585;
  if (!inMain && !inStem) return false;
  // Exclude the longitudinal fissure gap (wider strip for visible separation)
  if (Math.abs(dx) < 12 && dy < 160) return false;
  return true;
}

// ─── Generate background neural points ──────────────────────────────────────
function generateMeshPoints(count: number): NeuralPt[] {
  const pts: NeuralPt[] = [];
  let attempts = 0;
  while (pts.length < count && attempts < count * 12) {
    attempts++;
    const x = CX + (Math.random() - 0.5) * 840;
    const y = CY + (Math.random() - 0.5) * 530;
    if (!insideBrain(x, y)) continue;
    // Check minimum distance (~22px) to avoid clumping
    const tooClose = pts.some(p => Math.hypot(p.x-x, p.y-y) < 22);
    if (tooClose) continue;
    const speed = 0.04 + Math.random() * 0.08;
    const angle = Math.random() * Math.PI * 2;
    pts.push({
      x, y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      baseAlpha: 0.55 + Math.random() * 0.45,
      phase: Math.random() * Math.PI * 2,
      region: null,
      r: 2.2 + Math.random() * 1.8,
      rgb: [180, 210, 255],
    });
  }
  return pts;
}

// ─── Build adjacency list for connections (within threshold) ─────────────────
function buildEdges(pts: NeuralPt[], threshold: number): [number,number][] {
  const edges: [number,number][] = [];
  for (let i = 0; i < pts.length; i++) {
    for (let j = i + 1; j < pts.length; j++) {
      const d = Math.hypot(pts[j].x-pts[i].x, pts[j].y-pts[i].y);
      if (d < threshold) edges.push([i, j]);
    }
  }
  return edges;
}

// ─── Main Component ──────────────────────────────────────────────────────────
export const BrainGraph: React.FC<Props> = ({
  nodes, regionStats, coverage, conflicts, highImpact, onNodeClick, onAddNode,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<number>(0);
  const timeRef = useRef(0);
  const ptsRef = useRef<NeuralPt[]>([]);
  const edgesRef = useRef<[number,number][]>([]);
  const brainPathRef = useRef<Path2D | null>(null);

  const [hovered, setHovered] = useState<BrainNode|null>(null);
  const [hoveredRegion, setHoveredRegion] = useState<string|null>(null);
  const [filter, setFilter] = useState<'all'|'confirmed'|'inferred'|'conflicts'|'high_impact'>('all');

  // ── Filter data nodes ────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const hi = new Set(highImpact.map(n=>n.id));
    const co = new Set(conflicts.flatMap(c=>[c.nodeA.id,c.nodeB.id]));
    return nodes.filter(n => {
      if (filter==='confirmed')   return n.source==='confirmed';
      if (filter==='inferred')    return n.source==='inferred';
      if (filter==='conflicts')   return co.has(n.id);
      if (filter==='high_impact') return hi.has(n.id);
      return true;
    });
  }, [nodes, filter, highImpact, conflicts]);

  // ── Build full point set (mesh + data nodes) ─────────────────────────────
  useEffect(() => {
    // Background neural mesh
    const mesh = generateMeshPoints(220);

    // Data nodes — positioned near region anchor + organic offset
    const dataOffsets = [
      [0,0],[-20,-16],[22,-10],[-14,20],[26,18],[-28,2],[30,-2],
      [0,-28],[2,26],[-25,-25],[28,25],
    ];
    const dataPts: NeuralPt[] = [];
    const byRegion = new Map<string, BrainNode[]>();
    for (const n of filtered) {
      const l = byRegion.get(n.region) || [];
      l.push(n); byRegion.set(n.region, l);
    }
    for (const [reg, rn] of byRegion) {
      const pos = REGIONS[reg]; if (!pos) continue;
      rn.forEach((node, i) => {
        const off = dataOffsets[i] || [((i%3)-1)*20, ((i%2)*2-1)*16];
        const x = pos.cx + off[0], y = pos.cy + off[1];
        dataPts.push({
          x, y, vx:0, vy:0,
          baseAlpha: 1,
          phase: Math.random() * Math.PI * 2,
          region: reg,
          dataNode: node,
          r: 4 + node.confidence * 5,
          rgb: pos.rgb,
        });
      });
    }

    // Region anchor nodes (always shown, even if no data)
    const regionAnchors: NeuralPt[] = Object.entries(REGIONS).map(([key, pos]) => ({
      x: pos.cx, y: pos.cy, vx: 0, vy: 0,
      baseAlpha: 1,
      phase: Math.random() * Math.PI * 2,
      region: key,
      r: 5.5,
      rgb: pos.rgb,
    }));

    ptsRef.current = [...mesh, ...regionAnchors, ...dataPts];
    edgesRef.current = buildEdges(ptsRef.current, 108);
  }, [filtered]);

  // ── Canvas resize handler ─────────────────────────────────────────────────
  const [dims, setDims] = useState({w: CANVAS_W, h: CANVAS_H});
  useEffect(() => {
    const obs = new ResizeObserver(entries => {
      for (const e of entries) {
        const {width, height} = e.contentRect;
        setDims({w: Math.round(width), h: Math.round(height)});
      }
    });
    if (containerRef.current) obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  // ── Render loop ──────────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Build Path2D for brain silhouette (used for hit-testing only)
    const brain = new Path2D();
    buildBrainPath(brain);
    brainPathRef.current = brain;

    // Load brain background image
    const bgImg = new Image();
    bgImg.src = '/brain-hologram.png?v=2';
    let imgReady = false;
    bgImg.onload = () => { imgReady = true; };

    let last = 0;
    const EDGE_THRESHOLD = 108;

    const draw = (ts: number) => {
      const dt = Math.min(ts - last, 50);
      last = ts;
      timeRef.current += dt * 0.001;
      const t = timeRef.current;

      const cw = canvas.width, ch = canvas.height;
      const scaleX = cw / CANVAS_W, scaleY = ch / CANVAS_H;

      ctx.clearRect(0, 0, cw, ch);

      // Scale everything to canvas size
      ctx.save();
      ctx.scale(scaleX, scaleY);

      // ── Background ──────────────────────────────────────────────────
      ctx.fillStyle = '#000408';
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

      // ── Brain hologram image ────────────────────────────────────────
      if (imgReady) {
        const imgW = 880, imgH = 700;
        const imgX = CX - imgW / 2;
        const imgY = CY - imgH / 2 - 10;
        ctx.globalAlpha = 0.5;
        ctx.drawImage(bgImg, imgX, imgY, imgW, imgH);
        ctx.globalAlpha = 1.0;
      }

      // ── Update node positions (gentle drift inside brain) ───────────
      const pts = ptsRef.current;
      for (const p of pts) {
        if (p.region !== null) continue; // data/anchor nodes don't drift
        p.x += p.vx;
        p.y += p.vy;
        // Reflect if outside brain (approximate)
        if (!insideBrain(p.x, p.y)) {
          p.vx *= -1; p.vy *= -1;
          p.x += p.vx * 2; p.y += p.vy * 2;
        }
      }

      // ── Draw mesh connections & nodes (clipped to brain contour, 10% opacity) ──
      ctx.save();
      ctx.clip(brain);
      ctx.globalAlpha = 0.10;

      const edges = edgesRef.current;
      for (const [i, j] of edges) {
        const a = pts[i], b = pts[j];
        // Skip edges that connect data/anchor nodes (drawn later at full opacity)
        if ((a.dataNode || a.region) && (b.dataNode || b.region)) continue;
        const d = Math.hypot(b.x-a.x, b.y-a.y);
        const edgeAlpha = (1 - d / EDGE_THRESHOLD) * 0.45;

        ctx.save();
        ctx.globalAlpha = 0.10;
        const grad = ctx.createLinearGradient(a.x, a.y, b.x, b.y);
        grad.addColorStop(0, `rgba(180,210,255,${edgeAlpha})`);
        grad.addColorStop(1, `rgba(180,210,255,${edgeAlpha})`);
        ctx.strokeStyle = grad;
        ctx.lineWidth = 0.7;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
        ctx.restore();
      }

      // Mesh nodes (background only, not data/anchor)
      for (const p of pts) {
        if (p.dataNode || p.region !== null) continue;
        const pulse = 0.7 + 0.3 * Math.sin(t * 2.2 + p.phase);
        const [r, g, b_] = p.rgb;
        const nodeGrad = ctx.createRadialGradient(p.x - p.r*.25, p.y - p.r*.25, 0, p.x, p.y, p.r);
        const meshAlpha = p.baseAlpha * pulse;
        nodeGrad.addColorStop(0, `rgba(255,255,255,${meshAlpha})`);
        nodeGrad.addColorStop(0.4, `rgba(${r},${g},${b_},${meshAlpha * 0.9})`);
        nodeGrad.addColorStop(1, `rgba(${r},${g},${b_},${meshAlpha * 0.4})`);
        ctx.fillStyle = nodeGrad;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI*2);
        ctx.fill();
      }

      ctx.restore(); // restore clip + globalAlpha

      // ── Draw data/anchor nodes & their edges (full opacity, no clip) ────
      for (const [i, j] of edges) {
        const a = pts[i], b = pts[j];
        const isData = (a.dataNode || a.region) && (b.dataNode || b.region);
        if (!isData) continue;
        const d = Math.hypot(b.x-a.x, b.y-a.y);
        const edgeAlpha = (1 - d / EDGE_THRESHOLD) * 0.45;
        const aRgb = a.dataNode ? a.rgb : (a.region ? REGIONS[a.region]?.rgb || [180,210,255] : [180,210,255]);
        const bRgb = b.dataNode ? b.rgb : (b.region ? REGIONS[b.region]?.rgb || [180,210,255] : [180,210,255]);

        ctx.save();
        const grad = ctx.createLinearGradient(a.x, a.y, b.x, b.y);
        grad.addColorStop(0, `rgba(${aRgb[0]},${aRgb[1]},${aRgb[2]},${edgeAlpha * 1.8})`);
        grad.addColorStop(1, `rgba(${bRgb[0]},${bRgb[1]},${bRgb[2]},${edgeAlpha * 1.8})`);
        ctx.strokeStyle = grad;
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
        ctx.restore();
      }

      for (const p of pts) {
        if (!p.dataNode && p.region === null) continue;
        const pulse = 0.7 + 0.3 * Math.sin(t * 2.2 + p.phase);
        const isData = !!p.dataNode;
        const isAnchor = p.region !== null && !p.dataNode;
        const [r, g, b_] = p.rgb;

        if (isData || isAnchor) {
          // Outer glow
          const glow = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * (isAnchor ? 5 : 4));
          glow.addColorStop(0, `rgba(${r},${g},${b_},${0.4 * pulse})`);
          glow.addColorStop(1, `rgba(${r},${g},${b_},0)`);
          ctx.fillStyle = glow;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.r * (isAnchor ? 5 : 4), 0, Math.PI*2);
          ctx.fill();
        }

        // Core node
        const nodeGrad = ctx.createRadialGradient(p.x - p.r*.25, p.y - p.r*.25, 0, p.x, p.y, p.r);
        const alpha = pulse;
        nodeGrad.addColorStop(0, `rgba(255,255,255,${alpha})`);
        nodeGrad.addColorStop(0.4, `rgba(${r},${g},${b_},${alpha * 0.9})`);
        nodeGrad.addColorStop(1, `rgba(${r},${g},${b_},${alpha * 0.6})`);
        ctx.fillStyle = nodeGrad;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * 1.2, 0, Math.PI*2);
        ctx.fill();
      }

      // (Brain outline glow removed — the hologram image has its own glow)

      // ── Region labels ──────────────────────────────────────────────
      for (const [key, pos] of Object.entries(REGIONS)) {
        const stat = regionStats.find(r => r.region === key);
        const cnt = stat?.nodeCount ?? 0;
        const isHov = hoveredRegion === key;

        ctx.save();
        // Label
        ctx.font = `${isHov?'700':'600'} 9.5px "SF Mono", "Fira Code", monospace`;
        ctx.letterSpacing = '2px';
        ctx.textAlign = 'center';

        const labelY = pos.cy - 52 - (cnt > 0 ? 4 : 0);

        // Label glow
        if (cnt > 0 || isHov) {
          ctx.shadowColor = pos.color;
          ctx.shadowBlur = isHov ? 16 : 8;
        }
        ctx.fillStyle = cnt > 0 ? pos.color : 'rgba(150,160,180,0.4)';
        ctx.fillText(pos.label.toUpperCase(), pos.cx, labelY);

        // Count badge
        if (cnt > 0) {
          ctx.shadowBlur = 0;
          ctx.font = '600 8px "SF Mono", monospace';
          ctx.fillStyle = `${pos.color}90`;
          ctx.fillText(`${cnt}`, pos.cx, labelY + 13);
        }
        ctx.restore();
      }

      ctx.restore(); // restore scale
      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [dims, regionStats, hoveredRegion]);

  // ── Mouse interaction — detect hover over data nodes / regions ─────────
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scaleX = CANVAS_W / rect.width;
    const scaleY = CANVAS_H / rect.height;
    const mx = (e.clientX - rect.left) * scaleX;
    const my = (e.clientY - rect.top) * scaleY;

    // Check data nodes first
    let foundNode: BrainNode | null = null;
    let foundRegion: string | null = null;

    for (const p of ptsRef.current) {
      const d = Math.hypot(p.x - mx, p.y - my);
      if (p.dataNode && d < p.r + 8) { foundNode = p.dataNode; foundRegion = p.region; break; }
      if (p.region && !p.dataNode && d < p.r + 12) { foundRegion = p.region; }
    }
    setHovered(foundNode);
    setHoveredRegion(foundRegion);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHovered(null);
    setHoveredRegion(null);
  }, []);

  const handleClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scaleX = CANVAS_W / rect.width;
    const scaleY = CANVAS_H / rect.height;
    const mx = (e.clientX - rect.left) * scaleX;
    const my = (e.clientY - rect.top) * scaleY;

    for (const p of ptsRef.current) {
      const d = Math.hypot(p.x - mx, p.y - my);
      if (p.dataNode && d < p.r + 8) { onNodeClick?.(p.dataNode); return; }
      if (p.region && !p.dataNode && d < p.r + 16) {
        const stat = regionStats.find(r=>r.region===p.region);
        if (!(stat?.nodeCount)) onAddNode?.(p.region!);
        return;
      }
    }
  }, [onNodeClick, onAddNode, regionStats]);

  return (
    <div className="flex flex-col h-full bg-black">
      {/* ── Filter bar ─────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-[#0d1f3a]/80 bg-[#020912]/90 backdrop-blur-sm flex-shrink-0">
        {(['all','confirmed','inferred','conflicts','high_impact'] as const).map(f=>(
          <button key={f} onClick={()=>setFilter(f)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-all duration-200 ${
              filter===f
                ? 'bg-hig-accent/15 border-hig-accent/40 text-hig-accent shadow-[0_0_10px_theme(colors.hig-accent/0.19)]'
                : 'bg-transparent border-[#1e3050]/60 text-slate-600 hover:text-slate-400 hover:border-slate-600/50'
            }`}>
            {f==='all'?`All · ${nodes.length}`:
             f==='confirmed'?'Confirmed':
             f==='inferred'?'Inferred':
             f==='conflicts'?`Conflicts · ${conflicts.length}`:
             `High Impact · ${highImpact.length}`}
          </button>
        ))}
        <div className="flex-1"/>
        <div className="flex items-center gap-2.5">
          <span className="text-xs text-slate-700 uppercase tracking-widest font-mono">Coverage</span>
          <div className="relative w-24 h-px bg-[#0d1f3a]">
            <div className="absolute inset-y-0 left-0 h-px transition-all"
              style={{
                width:`${coverage*100}%`,
                background:'linear-gradient(90deg,var(--accent),#67e8f9)',
                boxShadow:'0 0 8px #67e8f9',
              }}/>
          </div>
          <span className="text-xs font-mono text-hig-accent">{Math.round(coverage*100)}%</span>
        </div>
      </div>

      {/* ── Canvas ─────────────────────────────────────────────────────────── */}
      <div ref={containerRef} className="flex-1 relative overflow-hidden bg-black">
        <canvas
          ref={canvasRef}
          width={dims.w * (window.devicePixelRatio || 1)}
          height={dims.h * (window.devicePixelRatio || 1)}
          style={{width:'100%', height:'100%', cursor: hovered ? 'pointer' : hoveredRegion ? 'pointer' : 'default'}}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          onClick={handleClick}
        />

        {/* ── Hover tooltip ─────────────────────────────────────────────── */}
        {hovered && (
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 pointer-events-none"
            style={{maxWidth:400, minWidth:260}}>
            <div className="rounded-2xl border px-5 py-3.5 backdrop-blur-2xl"
              style={{
                background:'rgba(2,9,20,0.95)',
                borderColor:`${REGIONS[hovered.region]?.color||'#818cf8'}30`,
                boxShadow:`0 0 40px ${REGIONS[hovered.region]?.color||'var(--accent)'}20, inset 0 0 40px rgba(0,0,0,0.5)`,
              }}>
              <div className="flex items-center gap-3 mb-2.5">
                <span className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{
                    backgroundColor: TYPE_COLORS[hovered.type]||'#818cf8',
                    boxShadow:`0 0 14px ${TYPE_COLORS[hovered.type]||'#818cf8'}80`,
                  }}/>
                <span className="text-white font-semibold text-sm leading-snug">{hovered.value}</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                <span className="px-2 py-0.5 rounded bg-white/5 text-slate-400 text-xs capitalize">{hovered.type}</span>
                <span className="px-2 py-0.5 rounded text-xs capitalize font-medium"
                  style={{background:`${REGIONS[hovered.region]?.color||'#818cf8'}15`, color:REGIONS[hovered.region]?.color||'#818cf8'}}>
                  {hovered.region}
                </span>
                <span className="px-2 py-0.5 rounded bg-white/5 text-hig-accent text-xs font-mono">
                  {Math.round(hovered.confidence*100)}%
                </span>
                <span className={`px-2 py-0.5 rounded bg-white/5 text-xs capitalize ${
                  hovered.source==='confirmed'?'text-emerald-400':
                  hovered.source==='inferred'?'text-amber-400':'text-sky-400'}`}>
                  {hovered.source}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* ── Region hover label overlay ─────────────────────────────────── */}
        {hoveredRegion && !hovered && (() => {
          const stat = regionStats.find(r=>r.region===hoveredRegion);
          const cnt = stat?.nodeCount ?? 0;
          const pos = REGIONS[hoveredRegion];
          return (
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 pointer-events-none">
              <div className="rounded-xl border px-4 py-2 backdrop-blur-xl"
                style={{
                  background:'rgba(2,9,20,0.9)',
                  borderColor:`${pos?.color||'#818cf8'}30`,
                }}>
                <span className="text-xs font-mono" style={{color:pos?.color||'#818cf8'}}>
                  {pos?.label} · {cnt > 0 ? `${cnt} nodes` : 'Click to add'}
                </span>
              </div>
            </div>
          );
        })()}
      </div>

      {/* ── Legend ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-5 px-5 py-2 border-t border-[#0d1f3a]/60 bg-[#020912]/80 backdrop-blur-sm flex-shrink-0">
        {Object.entries(TYPE_COLORS).map(([t,c])=>(
          <div key={t} className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{backgroundColor:c, boxShadow:`0 0 6px ${c}60`}}/>
            <span className="text-xs text-slate-600 capitalize tracking-wide">{t}</span>
          </div>
        ))}
        <div className="flex-1"/>
        <span className="text-xs text-slate-700 font-mono tracking-widest">COGNITIVE · NEURAL · MAP</span>
      </div>
    </div>
  );
};

export default BrainGraph;
